import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import A5PaymentVoucher from './paymentVoucherA5';
import Navbar from './components/navbar';
import { ErrorAlert, SuccessAlert } from './components/alertBox';
import { ChevronDown, ChevronUp, Plus, Search, Banknote } from 'lucide-react';

// Main Payment Voucher Form Component
export default function PaymentVoucher() {

  const TRANSFER_METHODS = [
    { value: 'peoples_bank', label: "People's Bank" },
    { value: 'seylan_bank', label: 'Seylan Bank' },
    { value: 'cash', label: 'Cash' }
  ];

  const [formData, setFormData] = useState({
    date: '',
    voucherNo: '',
    payableTo: '',
    chequeDetails: '',
    poNo: '',
    grnNo: '',
    name: '',
    transferMethod: '',
    items: [{ description: '', amount: '', rs: '', cts: '' }],
    // preparedBy: '',
    // authorisedBy: '',
    // receiverSignature: ''
  });

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const [showVoucher, setShowVoucher] = useState(false);
  const [savedVoucherData, setSavedVoucherData] = useState(null);
  const [isLoadingVoucherNumber, setIsLoadingVoucherNumber] = useState(true);

  const [payableCache, setPayableCache] = useState(new Map()); // Cache for search results
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);


  // Dropdown specific state

  const [isOpen, setIsOpen] = useState(false);
  const [filteredPayees, setFilteredPayees] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const [saveIsLoading, setSaveIsLoading] = useState(false);
  const inputRef = useRef(null);

  const [isTransferMethodOpen, setIsTransferMethodOpen] = useState(false);
  const transferDropdownRef = useRef(null);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    getVoucherNumber()

    const userNameStr = localStorage.getItem('user')
    const userData = JSON.parse(userNameStr)
    const userName = userData.name

    // Check for pre-fill data from URL query params (from To-Be-Payment app)
    const prefillPayableTo = searchParams.get('payableTo');
    const prefillGrnNo = searchParams.get('grnNo');
    const prefillAmount = searchParams.get('amount');
    const prefillDescription = searchParams.get('description');
    const prefillDate = searchParams.get('invoiceDate');
    const prefillItemsRaw = searchParams.get('items');
    console.log('[PV prefill] raw items param:', prefillItemsRaw);

    // Multi-invoice mode: `items` is a JSON array of {description, rs, invoiceId}.
    // invoiceId is carried through (hidden on UI) so the backend can mark
    // each linked to-be-payment row as Paid after the voucher saves.
    let prefillItems = null;
    if (prefillItemsRaw) {
      try {
        const parsed = JSON.parse(prefillItemsRaw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          prefillItems = parsed.map(it => ({
            description: it.description || '',
            amount: '',
            rs: it.rs !== undefined && it.rs !== null ? String(it.rs) : '',
            cts: '',
            invoiceId: it.invoiceId || null
          }));
          console.log('[PV prefill] parsed', prefillItems.length, 'invoice row(s)');
        }
      } catch (e) {
        console.error('[PV prefill] Failed to parse items JSON:', e);
      }
    }

    setFormData(prev => ({
      ...prev,
      name: userName,
      ...(prefillPayableTo && { payableTo: prefillPayableTo }),
      ...(prefillGrnNo && { grnNo: prefillGrnNo }),
      ...(prefillDate && { date: prefillDate }),
      ...(prefillItems
        ? { items: prefillItems }
        : (prefillDescription || prefillAmount) ? {
            items: [{
              description: prefillDescription || '',
              amount: '',
              rs: prefillAmount || '',
              cts: ''
            }]
          } : {})
    }))



    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };


  }, []);

  useEffect(() => {

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
      if (transferDropdownRef.current && !transferDropdownRef.current.contains(event.target)) {
        setIsTransferMethodOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);

  }, [])
  useEffect(() => {

    const savedCache = localStorage.getItem('payableCache');
    if (savedCache) {
      try {
        const parsed = JSON.parse(savedCache);
        setPayableCache(new Map(Object.entries(parsed)));
      } catch (e) {
        console.error("Failed to load cache:", e);
      }
    }
  }, []);

  // Save cache to localStorage when it changes
  useEffect(() => {
    if (payableCache.size > 0) {
      const cacheObj = Object.fromEntries(payableCache);
      localStorage.setItem('payableCache', JSON.stringify(cacheObj));
    }
  }, [payableCache]);



  const getVoucherNumber = async () => {
    try {
      setIsLoadingVoucherNumber(true);
      const response = await fetch('https://payvoucher.netgateerp.lk/api/users/next-number')
      const data = await response.json()
      console.log("voucher number data:", data)
      if (data.success) {
        console.log("voucher number:", data.voucherNumber)
        setFormData(prev => ({
          ...prev,
          voucherNo: data.voucherNumber
        }));
      } else {
        console.error('Failed to fetch voucher number:', data.message);
        // Fallback to a timestamp-based number if API fails
        const timestamp = Date.now().toString().slice(-6);
        setFormData(prev => ({
          ...prev,
          voucherNo: timestamp
        }));
      }

    } catch (e) {
      console.log("Error retrieving voucher number", e)
    }
    finally {
      setIsLoadingVoucherNumber(false); // Set loading to false when done
    }
  }

  // Debounced search function
  const searchPayableNames = useCallback(async (searchTerm) => {
    // Clear any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const trimmedSearch = searchTerm;

    if (!trimmedSearch) {
      setFilteredPayees([]);
      setIsOpen(false);
      return;
    }

    // // 1. First, check cache for exact match
    // if (payableCache.has(trimmedSearch)) {
    //   console.log("Using cached results for:", trimmedSearch);
    //   const cachedNames = payableCache.get(trimmedSearch);

    //   setFilteredPayees(cachedNames);
    //   console.log("Filtered payees from cache:", cachedNames);
    //   setIsOpen(true);
    //   return;
    // }

    // // 2. Check if we can derive results from cached prefix searches
    // const cachedResult = findInCache(trimmedSearch, payableCache);
    // if (cachedResult) {
    //   console.log("Filtering from cached prefix:", cachedResult.key);
    //   const filtered = cachedResult.names.filter(name =>
    //     name.toLowerCase().includes(trimmedSearch)
    //   );
    //   console.log("Filtered payees from cached prefix:", filtered);

    //   // Cache this new search result
    //   setPayableCache(prev => new Map(prev).set(trimmedSearch, filtered));

    //   setFilteredPayees(filtered);
    //   setIsOpen(true);
    //   return;
    // }

    // 3. No cache hit - fetch from server
    console.log("Fetching from server for:", trimmedSearch);

    try {
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      const response = await fetch(
        `https://payvoucher.netgateerp.lk/api/users/getPayableName/${encodeURIComponent(searchTerm)}`,
        { signal: abortControllerRef.current.signal }
      );

      const data = await response.json();
      console.log("Payable name data:", data);

      if (!response.ok) {

        setFilteredPayees([]);
        setIsOpen(false);
        return;
      }

      const payableNames = Array.isArray(data)
        ? data.map(item => item.payableName)
        : [];

      console.log("Fetched payable names:", payableNames);

      // Cache the results
      setPayableCache(prev => new Map(prev).set(trimmedSearch, payableNames));

      // Limit cache size to prevent memory issues
      if (payableCache.size > 50) {
        const firstKey = payableCache.keys().next().value;
        setPayableCache(prev => {
          const newCache = new Map(prev);
          newCache.delete(firstKey);
          return newCache;
        });
      }


      setFilteredPayees(payableNames);
      setIsOpen(true);
      setHighlightedIndex(-1);

    } catch (e) {
      if (e.name === 'AbortError') {
        console.log("Request aborted");
        return;
      }
      console.error("Error fetching payable names:", e);

      setFilteredPayees([]);
      setIsOpen(false);
    }
  }, [payableCache]);

  // Helper function to find results in cache
  // const findInCache = (searchTerm, cache) => {
  //   // Look for any cached key that is a prefix of the search term
  //   for (const [key, names] of cache.entries()) {
  //     if (searchTerm.startsWith(key) && names.length > 0) {
  //       return { key, names };
  //     }
  //   }
  //   return null;
  // };

  // Debounced handler
  const handlePayableInput = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'payableTo') {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // For empty input, clear immediately without debounce
      if (!value.trim()) {
        setFilteredPayees([]);
        setIsOpen(false);
        return;
      }

      // Set new timer (300ms delay - adjust as needed)
      debounceTimerRef.current = setTimeout(() => {
        searchPayableNames(value);
      }, 300);
    } else {
      setFilteredPayees([]);
      setIsOpen(false);
    }
  }, [searchPayableNames]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({ ...prev, items: newItems }));

    // Clear item-specific errors
    if (errors.itemErrors && errors.itemErrors[`${index}_${field}`]) {
      const newItemErrors = { ...errors.itemErrors };
      delete newItemErrors[`${index}_${field}`];
      setErrors(prev => ({ ...prev, itemErrors: newItemErrors }));
    }

    // Clear general items error if an item now has content
    if (errors.items && (value.trim() || newItems[index].rs || newItems[index].cts)) {
      setErrors(prev => ({ ...prev, items: undefined }));
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', amount: '', rs: '', cts: '' }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: newItems }));

      // Clean up item errors for removed item
      if (errors.itemErrors) {
        const newItemErrors = { ...errors.itemErrors };
        Object.keys(newItemErrors).forEach(key => {
          if (key.startsWith(`${index}_`)) {
            delete newItemErrors[key];
          }
          // Adjust indices for items after the removed one
          if (key.startsWith(`${index + 1}_`)) {
            const newKey = key.replace(`${index + 1}_`, `${index}_`);
            newItemErrors[newKey] = newItemErrors[key];
            delete newItemErrors[key];
          }
        });
        setErrors(prev => ({ ...prev, itemErrors: newItemErrors }));
      }
    }
  };

  const insertPaymentVoucherData = async () => {
    setSaveIsLoading(true)
    try {
      const response = await fetch('https://payvoucher.netgateerp.lk/api/users/payment-vouchers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          totalRs,
          // totalCts,
          createdAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
        }),
      });

      if (!response.ok) {
        // throw new Error(`HTTP error! status: ${response.status}`);
        ErrorAlert("Failed to save payment voucher. Please try again.");
        setSaveIsLoading(false);
      }

      const result = await response.json();
      console.log('Payment voucher saved successfully:', result);
      setSaveIsLoading(false);
      return result;

    } catch (error) {
      console.error('Error inserting payment voucher data:', error);
      // ErrorAlert("Failed to save payment voucher. Please try again.");
      setSaveIsLoading(false);
      throw error;
    }
  };

  const handleSave = async () => {

    setShowErrors(true);

    // Call validateForm() to check if form is valid
    const isValid = validateForm();

    if (!isValid) {
      // If validation fails, don't proceed with saving
      return;
    }

    try {
      // Save to backend first
      await insertPaymentVoucherData();

      // Then save locally and show voucher
      setSavedVoucherData({ ...formData });
      setShowVoucher(true);

      // Optional: Show success message
      SuccessAlert("Payment voucher saved successfully!");

    } catch (error) {
      // Handle error - show error message to user
      ErrorAlert("Failed to save payment voucher. Please try again.");
      console.error('Save failed:', error);
    }
  };

  // Validation functions
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.grnNo) {
      newErrors.date = 'GRN number is required';
    }
    if (!formData.poNo) {
      newErrors.date = 'PO number is required';
    }

    if (!formData.payableTo.trim()) {
      newErrors.payableTo = 'Payable to is required';
    }

    if (!formData.chequeDetails.trim()) {
      newErrors.chequeDetails = 'Payment method details are required';
    }
    if (!formData.transferMethod.trim()) {
      newErrors.transferMethod = 'Transfer method is required';
    }


    // Items validation
    const validItems = formData.items.filter(item =>
      item.description.trim() || item.rs || item.cts
    );

    if (validItems.length === 0) {
      newErrors.items = 'At least one item is required';
    } else {
      const itemErrors = {};
      validItems.forEach((item, index) => {
        if (!item.description.trim()) {
          itemErrors[`${index}_description`] = 'Description is required';
        }
        if (!item.rs && !item.cts) {
          itemErrors[`${index}_amount`] = 'Amount is required';
        }
        if (item.rs && (isNaN(item.rs) || parseFloat(item.rs) < 0)) {
          itemErrors[`${index}_rs`] = 'Invalid amount';
        }
        if (item.cts && (isNaN(item.cts) || parseInt(item.cts) < 0 || parseInt(item.cts) > 99)) {
          itemErrors[`${index}_cts`] = 'Cents must be 0-99';
        }
      });

      if (Object.keys(itemErrors).length > 0) {
        newErrors.itemErrors = itemErrors;
      }
    }
    // Check if total amount is greater than 0
    const totalAmount = calculateTotal();
    if (totalAmount.rs === 0 && totalAmount.cts === 0 && validItems.length > 0) {
      newErrors.totalAmount = 'Total amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const clearForm = () => {
    setFormData({
      date: '',
      voucherNo: '',
      payableTo: '',
      chequeDetails: '',
      poNo: '',
      grnNo: '',
      transferMethod: '',
      items: [{ description: '', amount: '', rs: '', cts: '' }],
      // preparedBy: '',
      // authorisedBy: '',
      // receiverSignature: ''
    });
    setErrors({});
    setShowErrors(false);
  };

  const getInputClassName = (fieldName, baseClassName) => {
    const hasError = showErrors && errors[fieldName];
    return `${baseClassName} ${hasError ? 'border-red-500 bg-red-50' : ''}`;
  };

  const getItemInputClassName = (index, field, baseClassName) => {
    const hasError = showErrors && errors.itemErrors && errors.itemErrors[`${index}_${field}`];
    return `${baseClassName} ${hasError ? 'border-red-500 bg-red-50' : ''}`;
  };
  const calculateTotal = () => {
    let totalRs = 0;
    let totalCts = 0;

    formData.items.forEach(item => {
      const rs = parseFloat(item.rs) || 0;
      const cts = parseInt(item.cts) || 0;

      totalRs += rs;
      totalCts += cts;
    });

    // Convert cents overflow to rupees
    totalRs += Math.floor(totalCts / 100);
    // totalCts = totalCts % 100;

    return { rs: totalRs, };
  };

  const { rs: totalRs } = calculateTotal();

  const handleInputChange = async (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));



    // Clear specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle payee selection
  const handlePayeeSelect = (payee) => {
    console.log("Selected payee:", payee);
    setFormData(prev => ({ ...prev, payableTo: payee }));
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };


  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredPayees.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handlePayeeSelect(filteredPayees[highlightedIndex]);
        } else if (formData.payableTo.trim()) {
          // Add new payee
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        setIsOpen(false);
    }
  };





  return (
    <>
      <div className="w-full min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
        <Navbar />
        <div className="flex-1 p-4 overflow-x-hidden">
          <div className="border-2 border-blue-800 min-h-full bg-white shadow-lg max-w-full">
            {/* Header */}
            <div className="border-b-2 border-blue-800 p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start">
                <div className="w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center mb-4 sm:mb-0 sm:mr-8 mx-auto sm:mx-0">
                  <img
                    src="/netgatelogo.png"
                    alt="NetGate Lanka Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="text-blue-800 text-center flex-1 min-w-0">
                  <div className="font-bold text-xl sm:text-3xl mb-3 leading-tight">NetGate Lanka (Pvt) Ltd.</div>
                  <div className="text-sm sm:text-lg mb-2 leading-relaxed">No. 251/1A, Moratuwa Road, Piliyandala.</div>
                  <div className="text-sm sm:text-lg mb-6 leading-relaxed">Tel: 070 166 6777</div>
                  <div className="border-2 border-blue-800 inline-block px-4 sm:px-8 py-3 mt-2">
                    <div className="font-semibold text-lg sm:text-xl">PAYMENT VOUCHER</div>
                  </div>
                </div>
              </div>
            </div>




            {/* Form Fields */}
            <div className="p-4 sm:p-8 overflow-x-hidden">
              {/* Top row */}
              <div className="flex flex-col sm:flex-row sm:justify-between mb-8 gap-4">
                <div className="flex items-center">
                  <span className="text-sm mr-4 text-blue-800 font-medium min-w-[50px]">Date :</span>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className={getInputClassName('date', 'border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-36 sm:w-44 text-sm')}
                  />
                  {showErrors && errors.date && (
                    <span className="text-red-500 text-xs ml-3">{errors.date}</span>
                  )}
                </div>
                <div className="flex items-center">
                  <span className="text-sm mr-4 text-blue-800 font-medium">No.</span>
                  {isLoadingVoucherNumber ? (
                    <div className="w-24 px-2 py-2 text-sm text-gray-500 animate-pulse">
                      Loading...
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.voucherNo}
                      readOnly
                      className="border-b-2 border-dotted border-blue-600  px-2 py-2 w-24 font-bold text-sm cursor-not-allowed text-gray-700"
                      title="Auto-generated voucher number (read-only)"
                    />
                  )}
                  {showErrors && errors.voucherNo && (
                    <span className="text-red-500 text-xs ml-3">{errors.voucherNo}</span>
                  )}
                </div>
              </div>

              {/* Second row */}
              <div className="mb-8">
                <div className="flex items-center relative" ref={dropdownRef}>
                  <span className="text-sm mr-4 text-blue-800 font-medium min-w-[100px]">Payable to :</span>
                  {/* Wrapper div for input and dropdown icon */}
                  <div className="flex-1 ml-3 relative">
                    <input
                      type="text"
                      ref={inputRef}
                      value={formData.payableTo}
                      onChange={(e) => handlePayableInput('payableTo', e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (formData.payableTo) {
                          setIsOpen(true);
                        }
                      }}
                      className={getInputClassName('payableTo', 'border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-full text-sm min-w-0 pr-8')}
                      placeholder="Enter payee name"
                      autoComplete="off"
                    />
                    {/* Dropdown icon - positioned relative to the input wrapper */}
                    <ChevronDown
                      className={`absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600 cursor-pointer transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      onClick={() => {
                        setIsOpen(!isOpen);
                        if (!isOpen) {
                          inputRef.current?.focus();
                        }
                      }}
                    />

                    {/* Dropdown menu - positioned absolute relative to the input wrapper */}
                    {isOpen && (
                      <div
                        className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                        style={{
                          top: '100%',
                          left: 0,
                          right: 0,
                          minWidth: '100%'
                        }}
                      >
                        {filteredPayees.length > 0 ? (
                          <>
                            {filteredPayees.map((payee, index) => (
                              <div
                                key={payee}
                                className={`px-3 py-2 cursor-pointer text-sm flex items-center hover:bg-blue-50 ${index === highlightedIndex ? 'bg-blue-50' : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handlePayeeSelect(payee);
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Prevent input blur
                                }}
                                onMouseEnter={() => setHighlightedIndex(index)}
                              >
                                <Search className="w-3 h-3 text-gray-400 mr-2" />
                                <span className="truncate">{payee}</span>
                              </div>
                            ))}
                          </>
                        ) : formData.payableTo.trim() ? (
                          <div className="px-3 py-2 text-sm text-gray-600 flex items-center"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handlePayeeSelect(formData.payableTo);
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent input blur
                            }}>
                            <Plus className="w-3 h-3 text-green-500 mr-2" />
                            <span> New payee: "{formData.payableTo}"</span>
                          </div>
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            Start typing to search payees...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Your existing error display */}
                {showErrors && errors.payableTo && (
                  <div className="text-red-500 text-xs mt-2 ml-28">{errors.payableTo}</div>
                )}
              </div>

              {/* Transfer Method row - add after Cheque No./Cash/Online section */}
              <div className="mb-8">
                <div className="flex items-center relative" ref={transferDropdownRef}>
                  <span className="text-sm mr-4 text-blue-800 font-medium min-w-[140px] sm:min-w-[180px]">Transfer Method :</span>
                  <div className="flex-1 ml-3 relative">
                    <div
                      className={getInputClassName('transferMethod', `border-b-2 border-dotted border-blue-600 focus:border-blue-600 px-2 py-2 w-full text-sm min-w-0 pr-8 bg-white cursor-pointer flex items-center ${formData.transferMethod ? 'text-black' : 'text-gray-500'}`)}
                      onClick={() => setIsTransferMethodOpen(!isTransferMethodOpen)}
                    >
                      {formData.transferMethod ? (
                        <div className="flex items-center">
                          {formData.transferMethod === "peoples_bank" && (
                            <img src="/peoples_bank.png" alt="People's Bank" className="w-5 h-5 mr-2 object-contain" />
                          )}
                          {formData.transferMethod === "seylan_bank" && (
                            <img src="/seylan_bank.png" alt="Seylan Bank" className="w-5 h-5 mr-2 object-contain" />
                          )}
                          {formData.transferMethod === "cash" && (
                            <Banknote className="w-5 h-5 mr-2 text-green-600" />
                          )}
                          <span>
                            {TRANSFER_METHODS.find(m => m.value === formData.transferMethod)?.label || formData.transferMethod}
                          </span>
                        </div>
                      ) : (
                        "Select transfer method"
                      )}
                    </div>
                    <div
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
                      onClick={() => setIsTransferMethodOpen(!isTransferMethodOpen)}
                    >
                      {isTransferMethodOpen ? (
                        <ChevronUp className="w-4 h-4 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-blue-600" />
                      )}
                    </div>

                    {isTransferMethodOpen && (
                      <div
                        className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                        style={{
                          top: '100%',
                          left: 0,
                          right: 0,
                          minWidth: '100%'
                        }}
                      >
                        {TRANSFER_METHODS.map((method) => (
                          <div
                            key={method.value}
                            className="px-3 py-2 cursor-pointer text-sm flex items-center hover:bg-blue-50"
                            onClick={() => {
                              handleInputChange('transferMethod', method.value);
                              setIsTransferMethodOpen(false);
                            }}
                          >
                            {method.value === "peoples_bank" && (
                              <img src="/peoples_bank.png" alt="People's Bank" className="w-5 h-5 mr-3 object-contain" />
                            )}
                            {method.value === "seylan_bank" && (
                              <img src="/seylan_bank.png" alt="Seylan Bank" className="w-5 h-5 mr-3 object-contain" />
                            )}
                            {method.value === "cash" && (
                              <Banknote className="w-5 h-5 mr-3 text-green-600" />
                            )}
                            <span>{method.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {showErrors && errors.transferMethod && (
                  <div className="text-red-500 text-xs mt-2 ml-36 sm:ml-48">{errors.transferMethod}</div>
                )}
              </div>

              {/* Third row */}
              <div className="mb-8">
                <div className="flex items-center">
                  <span className="text-sm mr-4 text-blue-800 font-medium min-w-[140px] sm:min-w-[180px]">Cheque No./Cash/Online :</span>
                  <input
                    type="text"
                    value={formData.chequeDetails}
                    onChange={(e) => handleInputChange('chequeDetails', e.target.value)}
                    className={getInputClassName('chequeDetails', 'border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 flex-1 ml-3 text-sm min-w-0')}
                    placeholder="Enter payment method details"
                  />
                </div>
                {showErrors && errors.chequeDetails && (
                  <div className="text-red-500 text-xs mt-2 ml-36 sm:ml-48">{errors.chequeDetails}</div>
                )}
              </div>


              {/* Fourth row */}
              <div className="flex flex-col sm:flex-row sm:justify-between mb-10 gap-4">
                <div className="flex items-center">
                  <span className="text-sm mr-4 text-blue-800 font-medium">P.O. No.</span>
                  <input
                    type="text"
                    value={formData.poNo}
                    onChange={(e) => handleInputChange('poNo', e.target.value)}
                    className="border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-32 sm:w-36 ml-3 text-sm"
                    placeholder="P.O. Number"
                  />
                </div>
                <div className="flex items-center">
                  <span className="text-sm mr-4 text-blue-800 font-medium">G.R.N. No.</span>
                  <input
                    type="text"
                    value={formData.grnNo}
                    onChange={(e) => handleInputChange('grnNo', e.target.value)}
                    className="border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-32 sm:w-36 ml-3 text-sm"
                    placeholder="G.R.N. Number"
                  />
                </div>
              </div>

              {/* Items error */}
              {showErrors && errors.items && (
                <div className="text-red-500 text-sm mb-4 p-2 bg-red-50 border-l-4 border-red-500">{errors.items}</div>
              )}

              {/* Table */}
              <div className="border-2 border-blue-800 mb-10 shadow-sm overflow-x-auto">
                <div className="min-w-full">
                  {/* Table header */}
                  <div className=" text-center text-sm font-semibold bg-blue-50">
                    <div className="flex border-b-2 border-blue-800 min-w-[500px]">
                      <div className="flex-1 border-r-2 border-blue-800 py-4 min-w-[300px]">
                        Description
                      </div>
                      <div className="w-48 sm:w-64">
                        <div className="py-2 border-b border-blue-800">
                          Amount
                        </div>
                        <div className="flex">
                          <div className="flex-1 py-2 border-r-2 border-blue-800">
                            Rs
                          </div>
                          <div className="flex-1 py-2">
                            Cts
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table content */}
                  <div className="min-h-48 flex flex-col min-w-[500px]">
                    {/* Data rows */}
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex border-b border-blue-800 hover:bg-gray-50">
                        <div className="flex-1 border-r-2 border-blue-800 p-4 min-w-[300px]">
                          <textarea
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className={getItemInputClassName(index, 'description', 'w-full outline-none  bg-transparent text-sm resize-none')}
                            placeholder="Item description"
                            rows={2}
                            style={{ minHeight: '40px' }}
                          />
                          {showErrors && errors.itemErrors && errors.itemErrors[`${index}_description`] && (
                            <div className="text-red-500 text-xs mt-2">{errors.itemErrors[`${index}_description`]}</div>
                          )}
                        </div>
                        <div className="w-48 sm:w-64 flex">
                          <div className="flex-1 border-r-2 border-blue-800 p-4">
                            <input
                              type="number"
                              value={item.rs}
                              onChange={(e) => handleItemChange(index, 'rs', e.target.value)}
                              className={getItemInputClassName(index, 'rs', 'w-full outline-none py-2 text-center bg-transparent text-sm')}
                              placeholder="0"
                              step="0.01"
                              min="0"
                            />
                            {showErrors && errors.itemErrors && errors.itemErrors[`${index}_rs`] && (
                              <div className="text-red-500 text-xs mt-2 text-center">{errors.itemErrors[`${index}_rs`]}</div>
                            )}
                          </div>
                          <div className="flex-1 p-4 flex items-center">
                            <input
                              type="number"
                              value={item.cts}
                              onChange={(e) => handleItemChange(index, 'cts', e.target.value)}
                              className={getItemInputClassName(index, 'cts', 'w-full outline-none py-2 text-center bg-transparent text-sm')}
                              placeholder="0"
                              min="0"
                              max="99"
                            />
                            {formData.items.length > 1 && (
                              <button
                                onClick={() => removeItem(index)}
                                className="ml-3 text-red-500 hover:text-red-700 text-lg font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-100 flex-shrink-0"
                                title="Remove item"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          {showErrors && errors.itemErrors && errors.itemErrors[`${index}_cts`] && (
                            <div className="text-red-500 text-xs mt-2 text-center absolute">{errors.itemErrors[`${index}_cts`]}</div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Add item row */}
                    <div className="flex border-b border-blue-800 bg-blue-25">
                      <div className="flex-1 border-r-2 border-blue-800 p-4 min-w-[300px]">

                        <button
                          onClick={addItem}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-2 rounded hover:bg-blue-50 transition-colors"
                        >
                          + Add Item
                        </button>


                      </div>
                      <div className="w-48 sm:w-64 flex">
                        <div className="flex-1 border-r-2 border-blue-800 p-4"></div>
                        <div className="flex-1 p-4"></div>
                      </div>
                    </div>

                    {/* Empty space filler */}
                    <div className="flex flex-1">
                      <div className="flex-1 border-r-2 border-blue-800 min-w-[300px]"></div>
                      <div className="w-48 sm:w-64 flex">
                        <div className="flex-1 border-r-2 border-blue-800"></div>
                        <div className="flex-1"></div>
                      </div>
                    </div>
                  </div>

                  {/* Total row */}
                  <div className="border-t-2 border-blue-800 text-center text-sm font-bold bg-blue-100 min-w-[500px]">
                    <div className="flex">
                      <div className="flex-1 border-r-2 border-blue-800 py-4 text-blue-800 min-w-[300px]">
                        Total
                      </div>
                      <div className="w-48 sm:w-64 flex text-blue-800">
                        <div className="flex-1 border-r-2 border-blue-800 py-4">
                          {totalRs.toFixed(2)}
                        </div>
                        <div className="flex-1 py-4">
                          {/* {totalCts.toString().padStart(2, '0')} */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total amount error */}
              {showErrors && errors.totalAmount && (
                <div className="text-red-500 text-sm mb-6 p-3 bg-red-50 border-l-4 border-red-500">{errors.totalAmount}</div>
              )}

              {/* Bottom signature section */}
              {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-12 text-sm mt-12">
                <div className="text-center text-blue-800">
                  <input
                    type="text"
                    value={formData.preparedBy}
                    onChange={(e) => handleInputChange('preparedBy', e.target.value)}
                    className={getInputClassName('preparedBy', 'w-full border-b-2 border-dotted border-blue-600 focus:border-blue-600 text-black outline-none px-2 py-3 text-center mb-3')}
                    placeholder="Signature"
                  />
                  <div className="font-medium">Prepared by</div>
                  {showErrors && errors.preparedBy && (
                    <div className="text-red-500 text-xs mt-2">{errors.preparedBy}</div>
                  )}
                </div>
                <div className="text-center text-blue-800">
                  <input
                    type="text"
                    value={formData.authorisedBy}
                    onChange={(e) => handleInputChange('authorisedBy', e.target.value)}
                    className={getInputClassName('authorisedBy', 'w-full border-b-2 border-dotted border-blue-600 focus:border-blue-600 text-black outline-none px-2 py-3 text-center mb-3')}
                    placeholder="Signature"
                  />
                  <div className="font-medium">Authorised by</div>
                  {showErrors && errors.authorisedBy && (
                    <div className="text-red-500 text-xs mt-2">{errors.authorisedBy}</div>
                  )}
                </div>
                <div className="text-center text-blue-800">
                  <input
                    type="text"
                    value={formData.receiverSignature}
                    onChange={(e) => handleInputChange('receiverSignature', e.target.value)}
                    className={getInputClassName('receiverSignature', 'w-full border-b-2 border-dotted border-blue-600 focus:border-blue-600 text-black outline-none px-2 py-3 text-center mb-3')}
                    placeholder="Signature"
                  />
                  <div className="font-medium">Signature of Receiver</div>
                  {showErrors && errors.receiverSignature && (
                    <div className="text-red-500 text-xs mt-2">{errors.receiverSignature}</div>
                  )}
                </div>
              </div> */}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-4 sm:gap-6 mt-12 pt-6">
                <button
                  onClick={handleSave} disabled={saveIsLoading}
                  className="px-8 py-3 bg-[#1B2F69] text-white rounded-lg hover:bg-blue-800 transition-colors font-medium shadow-md"
                >
                  {saveIsLoading ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={clearForm}
                  className="px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium shadow-md"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Show ViewPaymentVoucher component when saved */}
      {showVoucher && savedVoucherData && (
        <A5PaymentVoucher
          voucherData={savedVoucherData}
          onClose={() => {
            setShowVoucher(false)
            setShowErrors(false);
            setFormData({
              date: '',
              voucherNo: '',
              payableTo: '',
              chequeDetails: '',
              poNo: '',
              grnNo: '',
              items: [{ description: '', amount: '', rs: '', cts: '' }],
              preparedBy: '',
              authorisedBy: '',
              receiverSignature: ''
            })
            getVoucherNumber();
          }

          }
        />
      )}
    </>
  );
}